import { Coach, CoachProvider, useCoach } from '@aj/coach';
import { studentPaths } from '@aj/core';
import type { StudentPath } from '@aj/core';
import { Box, HStack, TabBar, VStack, cx } from '@aj/ui';
import type { IconName, TabItem } from '@aj/ui';
import { useEffect, useMemo } from 'react';

import { useHideOnScroll } from './hooks/useHideOnScroll.js';
import { useNavigation } from './hooks/useNavigation.js';

import { Wordmark } from './components/Wordmark.js';
import { coachFlows } from './data/coachFlows.js';
import { coachStore } from './data/coachStore.js';
import { useActions, useCaptureTargets, useTodayView } from './data/hooks.js';
import { DESKTOP, useMediaQuery } from './hooks/useMediaQuery.js';
import { BAND_GAP, TOP_BAND } from './lib/layout.js';
import { useT } from './providers/I18nProvider.js';
import { Home } from './screens/Home.js';
import { NowSheet } from './screens/NowSheet.js';
import { Focus } from './screens/Focus.js';
import { Jatra } from './screens/Jatra.js';
import { Khata } from './screens/Khata.js';
import { Profile } from './screens/Profile.js';
import type { MessageKey } from '@aj/i18n';

/**
 * Shell. Four destinations and one action (§47); Today is the only one built.
 *
 * The split is load-bearing rather than cosmetic: Today carries ~80% of
 * sessions and the other three are meant to be rarely visited, because the app
 * is not the work. Any design where three tabs all want a daily visit is the
 * optimizer app wearing our colours.
 *
 * Tab state is plain `useState` — no router. Four screens with no deep links
 * and no back stack worth restoring; a router would cost more bundle than it
 * saves in code.
 */

const TABS: { id: string; icon: IconName; key: MessageKey }[] = [
  { id: 'today', icon: 'home', key: 'nav.today' },
  { id: 'jatra', icon: 'map', key: 'nav.jatra' },
  { id: 'khata', icon: 'book', key: 'nav.khata' },
  { id: 'me', icon: 'user', key: 'nav.me' },
];

/**
 * Wraps the shell in the coach, and decides WHEN each flow is offered.
 *
 * The trigger logic lives here, not in the package: "has seven days of
 * history" is a fact about this app's data, and a tutorial library that
 * knew how to compute it would not be liftable. The package only decides
 * whether the student can bear another interruption right now.
 */
export function App() {
  const t = useT();
  const capture = useCaptureTargets();
  const actions = useActions();
  const view = useTodayView();

  // Rebuilt whenever the capture targets change, which is how a step gets
  // copy about one specific habit without `@aj/coach` knowing what a habit is.
  const flows = useMemo(() => coachFlows(t, capture), [t, capture]);

  /**
   * Where an answer goes.
   *
   * The coach collects the words; the meaning is decided here. Every one of
   * these writes a field that intake deliberately did not ask for — the plan,
   * the floor, the obstacle — at the moment an actual experience finally made
   * the question answerable.
   */
  const onAnswer = (flowId: string, value: string) => {
    // Guarded rather than cast: the chip ids come from this file's own flow
    // definition today, but an answer restored from a store written by an
    // older version is the one case where the string could be anything.
    if (flowId === 'onboarding' && (studentPaths as readonly string[]).includes(value)) {
      actions.setProfilePath(value as StudentPath);
    }
    if (flowId === 'plan' && capture.plan) {
      actions.setHabitPlan(capture.plan.habitId, { anchor: value });
    }
    if (flowId === 'floor' && capture.floor) {
      actions.setHabitFloor(capture.floor.habitId, value);
    }
    if (flowId === 'obstacle' && view.goal) {
      actions.editGoal(view.goal.id, { obstacle: value });
    }
  };

  return (
    <CoachProvider flows={flows} store={coachStore} onAnswer={onAnswer}>
      <Shell />
      <Coach
        labels={{
          next: t('coach.next'),
          done: t('coach.done'),
          skip: t('coach.skip'),
          notNow: t('coach.notNow'),
        }}
      />
    </CoachProvider>
  );
}

function Shell() {
  const t = useT();
  const capture = useCaptureTargets();
  // Every one of these is a history entry, so the phone's Back button walks
  // them — out of a sheet, out of a session, back through the tabs, and only
  // then out of the app.
  const nav = useNavigation('today');
  const { tab, overlay } = nav;
  const desktop = useMediaQuery(DESKTOP);
  const hiddenBar = useHideOnScroll();
  const coach = useCoach();
  const view = useTodayView();

  /**
   * When each flow is offered.
   *
   * Conditions, never "first run". A tour fired on launch teaches the emptiest
   * version of the app, before any of it means anything; each of these waits
   * for the moment its subject becomes real.
   *
   * `offer` may decline — already seen, something else on screen, budget
   * spent — so calling it repeatedly is safe and is the intended usage.
   */
  useEffect(() => {
    if (view.habits.length === 0) {
      coach.offer('onboarding');
      return;
    }
    // The kept-rate bar is hidden until there is enough history for a
    // percentage to mean anything, so the first time it can be explained is
    // the first time it exists.
    if (view.commitment !== null) coach.offer('kept-rate');
  }, [coach, view.habits.length, view.commitment]);

  // A missed day is what makes the obstacle question answerable: the student
  // is recalling last night, not theorising about themselves in general.
  useEffect(() => {
    if (view.comeback) coach.offer('obstacle');
  }, [coach, view.comeback]);

  // Each of these waits for the experience that makes its question
  // answerable. The rhythm one fires around day two or three; the floor one
  // the morning after a real miss.
  useEffect(() => {
    if (capture.plan) coach.offer('plan');
  }, [coach, capture.plan]);

  useEffect(() => {
    if (capture.floor) coach.offer('floor');
  }, [coach, capture.floor]);


  const items: TabItem[] = TABS.map(({ id, icon, key }) => ({ id, icon, label: t(key) }));
  const action = { icon: 'bolt' as IconName, label: t('now.open'), onSelect: () => nav.open('now') };

  const screen = (
    <>
      {tab === 'today' && <Home onNavigate={nav.goTab} />}
      {tab === 'jatra' && <Jatra />}
      {tab === 'khata' && <Khata />}
      {tab === 'me' && <Profile />}
    </>
  );

  const sheet = overlay === 'now' && (
    <NowSheet
      onClose={nav.close}
      // Replaces the sheet's entry rather than stacking on it: Back from a
      // running session returns to the tab, not to the menu it was chosen from.
      onStartFocus={() => nav.replace('focus')}
    />
  );

  /**
   * A running session replaces the shell entirely — no tab bar, no wordmark,
   * nothing to browse. That is the strongest available statement of §46, and
   * it is why Focus is rendered here rather than inside a tab: a screen that
   * lives *beside* the navigation cannot take the navigation away.
   */
  if (overlay === 'focus') return <Focus onClose={nav.close} />;

  // Desktop stacks three rows: the wordmark alone at the top, then the
  // greeting band, then nav beside content. The nav column opens with a
  // spacer the height of that greeting band and the same gap the screen uses,
  // so the nav's top lands exactly on the chart's top — alignment by shared
  // measurements rather than by either side measuring the other.
  return desktop ? (
    <VStack gap={BAND_GAP} className="h-dvh w-full px-10 py-8">
      {/* Not a heading. The greeting on Home is this page's `h1`, and the
          wordmark being one too gave every desktop screen two — site identity
          is not what the page is about. */}
      <Box className="flex">
        <Wordmark />
      </Box>

      <HStack gap={8} align="stretch" className="min-h-0 flex-1">
        <VStack gap={BAND_GAP} className="w-28 shrink-0">
          <Box className={TOP_BAND} />
          <TabBar
            items={items}
            activeId={tab}
            onSelect={nav.goTab}
            action={action}
            orientation="vertical"
            className="w-20"
          />
        </VStack>

        <Box className="flex min-w-0 flex-1 flex-col">{screen}</Box>
      </HStack>
      {sheet}
    </VStack>
  ) : (
    <Box className="relative mx-auto flex h-dvh w-full max-w-md flex-col">
      {screen}
      {/* Floats above everything the screens stack — sticky day headers at
          z-10, the wall chart's lead column at z-20 — and stays below the Now
          sheet, which is allowed to cover it.

          It slides away while reading down and returns on the way up or on
          any tap. `focus-within` overrides that entirely: a keyboard user
          tabbing into the navigation must never be moving it off screen.
          Reduced motion pins it in place rather than slowing it down — a bar
          that comes and goes IS the motion, so there is nothing to slow. */}
      <TabBar
        items={items}
        activeId={tab}
        onSelect={nav.goTab}
        action={action}
        className={cx(
          'absolute inset-x-6 bottom-4 z-40 will-change-transform',
          'transition-transform duration-[var(--aj-duration-base)] ease-standard',
          'focus-within:translate-y-0 motion-reduce:translate-y-0 motion-reduce:transition-none',
          hiddenBar && 'translate-y-[calc(100%+1.5rem)]',
        )}
      />
      {sheet}
    </Box>
  );
}
